import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import Header from "../components/Header";
import TourCard from "../components/TourCard";
import { fetchTour } from "../common/api";
import { useTour } from "../context/TourContext";
import IMG_CARD1 from "../assets/card_1.png";

export default function TourDetailPage() {
  const { tourId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { selectedDate } = useTour();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [copied, setCopied] = useState(false);
  const date = searchParams.get("date") || selectedDate;

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);

    fetchTour(tourId)
      .then((data) => {
        if (!active) return;
        setTour(data);
        document.title = `${data.title} | ODK`;
      })
      .catch((requestError) => {
        if (!active) return;
        console.error(requestError);
        setError("We couldn't load this tour. Please try again.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });

    return () => {
      active = false;
      document.title = "ODK";
    };
  }, [reloadKey, tourId]);

  async function shareTour() {
    const shareData = { title: tour?.title ?? "ODK Tour", url: window.location.href };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(shareData.url);
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      }
    } catch (shareError) {
      if (shareError?.name !== "AbortError") console.error("Unable to share tour:", shareError);
    }
  }

  return (
    <div className="min-h-screen bg-gray-100 font-poppins">
      <Header dark />
      <main className="mx-auto w-full max-w-[980px] px-10 pb-20 pt-[104px] md:px-4 md:pb-10 md:pt-[88px]">
        <div className="mb-6 flex items-center justify-between gap-4">
          <button type="button" onClick={() => navigate("/")} className="border-0 bg-transparent p-0 text-[13px] font-medium text-gray-500 hover:text-black">
            &larr; All tours
          </button>
          {tour && (
            <button type="button" onClick={shareTour} className="rounded-full border border-gray-200 bg-white px-4 py-2 text-[12px] font-semibold text-gray-600 shadow-sm hover:border-[#E8876A] hover:text-[#E8876A]">
              {copied ? "Link copied" : "Share tour"}
            </button>
          )}
        </div>

        {loading ? (
          <div className="grid aspect-[3.55/1] grid-cols-2 overflow-hidden rounded-[36px] bg-white shadow-xl md:aspect-auto md:grid-cols-1" aria-label="Loading tour">
            <div className="animate-pulse bg-gray-200 md:aspect-video" />
            <div className="space-y-4 p-10 md:p-6">
              <div className="h-7 w-2/3 animate-pulse rounded bg-gray-200" />
              <div className="h-3 w-full animate-pulse rounded bg-gray-100" />
              <div className="h-3 w-4/5 animate-pulse rounded bg-gray-100" />
            </div>
          </div>
        ) : error ? (
          <div className="rounded-[28px] bg-white px-6 py-16 text-center shadow-xl" role="alert">
            <h1 className="m-0 text-[20px] font-semibold text-gray-800">Unable to load tour</h1>
            <p className="mb-6 mt-2 text-[13px] text-gray-400">{error}</p>
            <button type="button" onClick={() => setReloadKey((key) => key + 1)} className="rounded-full border-0 bg-[#2C2C2A] px-6 py-2.5 text-[13px] font-semibold text-white">Try again</button>
          </div>
        ) : tour ? (
          <TourCard
            tourId={tour.id ?? tourId}
            images={tour.images}
            title={tour.title}
            tags={tour.tags}
            description={tour.description}
            participantCount={tour.participant_count ?? tour.participantCount ?? 0}
            imgSrc={IMG_CARD1}
            width={900}
            selectedDate={date}
            defaultOpen
            standalone
          />
        ) : null}
      </main>
    </div>
  );
}
